import { t } from "@lingui/core/macro";
import { Plural, Trans } from "@lingui/react/macro";
import { Button, Chip } from "@heroui/react";
import dayjs from "dayjs";
import { Repeat } from "lucide-react";
import { useState } from "react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeInputLabel } from "@/Components/NextBase/LimeInputLabel";
import { LimeMultipleDayCalendar } from "@/Components/NextBase/LimeMultipleDayCalendar";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { api } from "@/lib/api-client";
import { cn } from "@/utils";
import {
  describeRepeat,
  formatDateWithYear,
  formatShortDate,
  toDate,
  toYmd,
  weekdayLetters,
  weekdayShortNames,
} from "./timeOffFormat";
import {
  type RepeatOption,
  repeatUsesEndDate,
  repeatUsesWeekdays,
  toggleWeekday,
} from "./timeOffFormValues";
import { Reveal, Section } from "./FormReveal";
import type { TimeOffFormState } from "./useTimeOffForm";

export function TimeOffRepeatSection({
  state,
  locationId,
}: {
  state: TimeOffFormState;
  locationId: number | null;
}) {
  const { form, values, setRepeat } = state;
  const [month, setMonth] = useState(() => toDate(values.date));
  const today = dayjs().format("YYYY-MM-DD");

  const monthStart = dayjs(month).startOf("month").format("YYYY-MM-DD");
  const monthEnd = dayjs(month).endOf("month").format("YYYY-MM-DD");
  const { data: shiftDates } = api.timeoff.useGetTimeOffShiftDates(
    values.repeat === "DATES" && locationId != null && values.userIds.length > 0
      ? {
          locationId,
          userIds: values.userIds.join(","),
          dateFrom: monthStart,
          dateTo: monthEnd,
        }
      : null,
  );
  const rostered = new Set(shiftDates?.dates ?? []);

  const items: { key: RepeatOption; label: string }[] = [
    { key: "NONE", label: t`Se ne ponavlja` },
    { key: "DAILY", label: t`Vsak dan` },
    { key: "WEEKLY", label: t`Vsak teden` },
    { key: "BIWEEKLY", label: t`Vsaka 2 tedna` },
    { key: "MONTHLY", label: t`Vsak mesec` },
    { key: "CUSTOM", label: t`Po meri…` },
    { key: "DATES", label: t`Na izbrane datume` },
  ];

  const targets = values.userIds.length + values.resourceIds.length;
  const showSummary =
    state.timePreview != null && (values.repeat !== "NONE" || targets > 1);

  return (
    <>
      <Section>
        <LimeSelect
          label={t`Ponavljanje`}
          data-identifier="time-off-repeat"
          disallowEmptySelection
          items={items}
          value={values.repeat}
          onSingleItemChange={(value) => setRepeat(value as RepeatOption)}
        />
      </Section>

      <Reveal isOpen={repeatUsesWeekdays(values.repeat)}>
        <div className="flex flex-col gap-1">
          <LimeInputLabel label={t`Dnevi v tednu`} />
          <div className="flex flex-wrap gap-1">
            {weekdayLetters().map((letter, index) => {
              const isOn = (values.weekdayMask & (1 << index)) !== 0;
              return (
                <Button
                  key={index}
                  type="button"
                  size="sm"
                  isIconOnly
                  radius="full"
                  variant={isOn ? "solid" : "bordered"}
                  color={isOn ? "primary" : "default"}
                  aria-pressed={isOn}
                  aria-label={weekdayShortNames()[index]}
                  data-identifier={`time-off-weekday-${index}`}
                  onPress={() =>
                    form.setFieldValue(
                      "weekdayMask",
                      toggleWeekday(values.weekdayMask, index),
                    )
                  }
                >
                  {letter}
                </Button>
              );
            })}
          </div>
          <Reveal isOpen={form.errors.weekdayMask != null} spacing="">
            <p className="text-danger text-tiny">{form.errors.weekdayMask}</p>
          </Reveal>
        </div>
      </Reveal>

      <Reveal isOpen={values.repeat === "CUSTOM"}>
        <div className="flex items-center gap-2">
          <span className="text-sm">
            <Trans>Vsakih</Trans>
          </span>
          <LimeNumberInput
            aria-label={t`Število tednov`}
            className="w-24"
            step={1}
            minValue={1}
            maxValue={52}
            value={values.customInterval}
            onValueChange={(value: number) =>
              form.setFieldValue(
                "customInterval",
                Number.isFinite(value) && value >= 1 ? Math.round(value) : 1,
              )
            }
            data-identifier="time-off-custom-interval"
          />
          <span className="text-sm">
            <Trans>tednov</Trans>
          </span>
        </div>
      </Reveal>

      <Reveal isOpen={repeatUsesEndDate(values.repeat)}>
        <LimeInput
          type="date"
          label={t`Ponavljaj do`}
          data-identifier="time-off-repeat-until"
          value={values.endsOn}
          onValueChange={(value) => form.setFieldValue("endsOn", value)}
          error={state.errorFor("endsOn")}
        />
      </Reveal>

      <Reveal isOpen={values.repeat === "DATES"}>
        <div
          className="flex flex-col gap-2"
          data-identifier="time-off-picked-dates"
        >
          <div className="flex justify-center">
            <LimeMultipleDayCalendar
              mode="multiple"
              month={month}
              onMonthChange={setMonth}
              selected={values.pickedDates.map(toDate)}
              onSelect={(dates: Date[] | undefined) =>
                form.setFieldValue(
                  "pickedDates",
                  (dates ?? []).map(toYmd).sort(),
                )
              }
              disabled={{ before: toDate(today) }}
              modifiers={{
                noShift: (date: Date) =>
                  shiftDates != null && !rostered.has(toYmd(date)),
              }}
              modifiersClassNames={{ noShift: "opacity-40" }}
            />
          </div>
          <Reveal isOpen={values.pickedDates.length > 0} spacing="">
            <div className="flex flex-wrap gap-1">
              {values.pickedDates.map((date) => (
                <Chip
                  key={date}
                  size="sm"
                  variant="flat"
                  onClose={() =>
                    form.setFieldValue(
                      "pickedDates",
                      values.pickedDates.filter(
                        (candidate) => candidate !== date,
                      ),
                    )
                  }
                >
                  {formatShortDate(date)}
                </Chip>
              ))}
            </div>
          </Reveal>
          <Reveal isOpen={form.errors.pickedDates != null} spacing="">
            <p className="text-danger text-tiny">{form.errors.pickedDates}</p>
          </Reveal>
        </div>
      </Reveal>

      <Reveal isOpen={showSummary}>
        <TimeOffRepeatSummary state={state} />
      </Reveal>
    </>
  );
}

/** The rule in words, the window, the end and the dry run's occurrence count. */
function TimeOffRepeatSummary({ state }: { state: TimeOffFormState }) {
  const { values, timePreview } = state;
  const targets = values.userIds.length + values.resourceIds.length;
  if (timePreview == null) return null;

  const count = timePreview.occurrenceCount;
  const total = timePreview.occurrencesWritten;

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-lg bg-default-50 p-3 text-sm transition-opacity",
        state.isPreviewing && "opacity-60",
      )}
      data-identifier="time-off-repeat-summary"
    >
      <p className="flex items-center gap-2 font-medium">
        <Repeat size={14} className="shrink-0 text-default-500" />
        <span className="min-w-0">{describeRepeat(values)}</span>
      </p>
      <SummaryRow label={t`Čas`}>
        {values.startTime}–{values.endTime}
      </SummaryRow>
      {repeatUsesEndDate(values.repeat) && (
        <SummaryRow label={t`Do`}>
          {formatDateWithYear(values.endsOn)}
        </SummaryRow>
      )}
      <SummaryRow label={t`Ponovitve`}>
        <span className="font-medium">
          <Plural
            value={count}
            one="# ponovitev"
            two="# ponovitvi"
            few="# ponovitve"
            other="# ponovitev"
          />
        </span>
        {targets > 1 && (
          <span className="text-default-500">
            {" · "}
            <Trans>
              × {targets} ={" "}
              <Plural
                value={total}
                one="# vnos"
                two="# vnosa"
                few="# vnosi"
                other="# vnosov"
              />
            </Trans>
          </span>
        )}
      </SummaryRow>
    </div>
  );
}

function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-20 shrink-0 text-default-500 text-tiny leading-5">
        {label}
      </span>
      <span className="min-w-0 flex-1 leading-5">{children}</span>
    </div>
  );
}
